/**
 * 权限管理 Hook
 * 提供便捷的权限判断方法
 */

import { useMemo } from 'react'
import { useAuth } from '@/utils/auth'
import {
    hasMaterialPermission,
    hasAdminPermission,
    hasVehicleManagePermission,
    hasPartsManagePermission,
    getNormalizedPermission,
} from '@/utils/permission'

export function usePermission() {
    const { userInfo: _userInfo, effectiveRoleCode } = useAuth()

    const permissions = useMemo(() => {
        // 使用 effectiveRoleCode 来判断权限（考虑角色覆盖）
        const roleCode = effectiveRoleCode

        return {
            // 是否可以管理物料（车辆和配件的增删改）
            canManageMaterial: hasMaterialPermission(roleCode),

            // 是否是管理员
            isAdmin: hasAdminPermission(roleCode),

            // 是否可以管理车辆
            canManageVehicle: hasVehicleManagePermission(roleCode),

            // 是否可以管理配件
            canManageParts: hasPartsManagePermission(roleCode),

            // 角色代码
            roleCode: roleCode || 'employee',

            // 规范化的权限类型
            permissionType: getNormalizedPermission(roleCode),
        }
    }, [effectiveRoleCode])

    return permissions
}

/**
 * 权限控制组件 Props
 */
export interface PermissionGuardProps {
    /**
     * 权限检查函数
     */
    hasPermission: () => boolean
    /**
     * 有权限时显示的内容
     */
    children: React.ReactNode
    /**
     * 无权限时的后备内容（可选）
     */
    fallback?: React.ReactNode
}

/**
 * 权限控制组件
 * 根据权限条件渲染内容
 */
export function PermissionGuard({ hasPermission, children, fallback = null }: PermissionGuardProps) {
    if (hasPermission()) {
        return <>{children}</>
    }
    return <>{fallback}</>
}
